import React, { useState, useEffect } from 'react';
import { BarChart3, Users, MapPin, Shield, LogOut, Eye, Phone, Clock, TrendingUp } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function LoginForm({ onLogin }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      const { data } = await axios.post(`${API}/admin/login`, { email, password }, { withCredentials: true });
      onLogin(data);
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : 'Invalid credentials');
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen bg-[#1A2520] flex items-center justify-center px-4">
      <div className="w-full max-w-sm bg-[#FDFBF7] rounded-2xl p-8 shadow-2xl">
        <div className="text-center mb-6">
          <Shield className="w-10 h-10 text-[#2C4C3B] mx-auto mb-3" />
          <h1 className="font-serif text-2xl font-bold text-[#1A2520]">Admin Dashboard</h1>
          <p className="text-[#5C6A61] text-sm mt-1">Lakshmi Nivasa</p>
        </div>
        <form onSubmit={handleSubmit} className="space-y-4">
          <input data-testid="admin-email" value={email} onChange={(e) => setEmail(e.target.value)}
            placeholder="Email" type="email"
            className="w-full h-11 px-4 rounded-xl border border-[#E5DFD3] text-sm focus:outline-none focus:ring-2 focus:ring-[#2C4C3B]" />
          <input data-testid="admin-password" value={password} onChange={(e) => setPassword(e.target.value)}
            placeholder="Password" type="password"
            className="w-full h-11 px-4 rounded-xl border border-[#E5DFD3] text-sm focus:outline-none focus:ring-2 focus:ring-[#2C4C3B]" />
          {error && <p className="text-red-500 text-xs">{error}</p>}
          <button type="submit" disabled={loading} data-testid="admin-login-btn"
            className="w-full bg-[#2C4C3B] hover:bg-[#213A2D] text-white py-3 rounded-xl text-sm font-semibold disabled:opacity-60">
            {loading ? 'Logging in...' : 'Login'}
          </button>
        </form>
      </div>
    </div>
  );
}

function StatCard({ icon: Icon, label, value, color = '#2C4C3B' }) {
  return (
    <div className="bg-white rounded-xl p-5 border border-[#E5DFD3]">
      <Icon className="w-5 h-5 mb-2" style={{ color }} />
      <p className="text-[10px] text-[#5C6A61] uppercase tracking-wider">{label}</p>
      <p className="font-serif text-2xl font-bold text-[#1A2520] mt-1">{value}</p>
    </div>
  );
}

function LeadTable({ title, data, columns }) {
  if (!data?.length) return null;
  return (
    <div className="bg-white rounded-xl border border-[#E5DFD3] overflow-hidden">
      <div className="px-5 py-3 border-b border-[#E5DFD3] bg-[#F4F1EA]">
        <h3 className="font-serif text-sm font-bold text-[#1A2520]">{title}</h3>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead><tr className="border-b border-[#E5DFD3]">
            {columns.map(c => <th key={c.key} className="px-4 py-2 text-left text-[10px] text-[#5C6A61] uppercase tracking-wider font-medium">{c.label}</th>)}
          </tr></thead>
          <tbody>
            {data.map((row, i) => (
              <tr key={i} className="border-b border-[#F4F1EA] last:border-0 hover:bg-[#F4F1EA]/50">
                {columns.map(c => <td key={c.key} className="px-4 py-2.5 text-[#1A2520] text-xs">{c.render ? c.render(row) : row[c.key] || '—'}</td>)}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Dashboard({ admin, onLogout }) {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [alerts, setAlerts] = useState([]);
  const [plotOverrides, setPlotOverrides] = useState({});
  const [statusSaving, setStatusSaving] = useState(null);

  const authHeaders = admin.token ? { Authorization: `Bearer ${admin.token}` } : {};

  useEffect(() => {
    const fetchAll = async () => {
      try {
        const [statsRes, alertsRes, plotsRes] = await Promise.all([
          axios.get(`${API}/admin/stats`, { withCredentials: true, headers: authHeaders }),
          axios.get(`${API}/admin/pricing-alerts`, { withCredentials: true, headers: authHeaders }).catch(() => ({ data: [] })),
          axios.get(`${API}/admin/plots`, { withCredentials: true, headers: authHeaders }).catch(() => ({ data: [] })),
        ]);
        setStats(statsRes.data);
        setAlerts(alertsRes.data);
        const overrides = {};
        plotsRes.data.forEach(p => { overrides[p.plot_id] = p.status; });
        setPlotOverrides(overrides);
      } catch { }
      finally { setLoading(false); }
    };
    fetchAll();
  }, [admin.token]); // eslint-disable-line react-hooks/exhaustive-deps

  const updatePlotStatus = async (plotId, newStatus) => {
    setStatusSaving(plotId);
    try {
      await axios.patch(`${API}/admin/plots/${plotId}/status`, { status: newStatus }, { withCredentials: true, headers: authHeaders });
      setPlotOverrides(prev => ({ ...prev, [plotId]: newStatus }));
    } catch { }
    finally { setStatusSaving(null); }
  };

  const dismissAlert = async (alertId) => {
    try {
      await axios.patch(`${API}/admin/pricing-alerts/${alertId}/dismiss`, {}, { withCredentials: true, headers: authHeaders });
      setAlerts(prev => prev.filter(a => a.id !== alertId));
    } catch { }
  };

  if (loading) return <div className="min-h-screen bg-[#F4F1EA] flex items-center justify-center"><p className="text-[#5C6A61]">Loading...</p></div>;

  const leadCols = [
    { key: 'name', label: 'Name' },
    { key: 'phone', label: 'Phone', render: (r) => <a href={`tel:${r.phone}`} className="text-[#2C4C3B] font-medium">{r.phone}</a> },
    { key: 'source', label: 'Source' },
    { key: 'created_at', label: 'Date', render: (r) => new Date(r.created_at).toLocaleDateString() },
  ];

  const visitCols = [
    { key: 'name', label: 'Name' },
    { key: 'phone', label: 'Phone', render: (r) => <a href={`tel:${r.phone}`} className="text-[#2C4C3B] font-medium">{r.phone}</a> },
    { key: 'plot_id', label: 'Plot', render: (r) => r.plot_id ? `#${r.plot_id}` : '—' },
    { key: 'preferred_date', label: 'Pref. Date' },
    { key: 'status', label: 'Status' },
  ];

  const vaastuCols = [
    { key: 'name', label: 'Name' },
    { key: 'phone', label: 'Phone', render: (r) => <a href={`tel:${r.phone}`} className="text-[#D48C57] font-bold">{r.phone}</a> },
    { key: 'plot_id', label: 'Plot', render: (r) => <span className="text-amber-600 font-bold">#{r.plot_id}</span> },
    { key: 'tag', label: 'Tag' },
    { key: 'created_at', label: 'Date', render: (r) => new Date(r.created_at).toLocaleDateString() },
  ];

  return (
    <div className="min-h-screen bg-[#F4F1EA]">
      {/* Header */}
      <header className="bg-[#2C4C3B] text-white px-6 py-4 flex items-center justify-between">
        <div>
          <h1 className="font-serif text-lg font-bold">Lakshmi Nivasa Admin</h1>
          <p className="text-white/60 text-xs">{admin.email}</p>
        </div>
        <button onClick={onLogout} className="flex items-center gap-1.5 text-white/70 hover:text-white text-sm">
          <LogOut className="w-4 h-4" /> Logout
        </button>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-6">
        {/* Stats */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          <StatCard icon={Users} label="Total Leads" value={stats?.counts?.leads || 0} />
          <StatCard icon={MapPin} label="Site Visits" value={stats?.counts?.site_visits || 0} color="#D48C57" />
          <StatCard icon={Shield} label="Vaastu Claims" value={stats?.counts?.vaastu_claims || 0} color="#D97706" />
          <StatCard icon={Eye} label="Plot Interactions" value={stats?.counts?.interactions || 0} color="#5C6A61" />
        </div>

        {/* Top Plots */}
        {stats?.top_plots?.length > 0 && (
          <div className="bg-white rounded-xl border border-[#E5DFD3] p-5">
            <h3 className="font-serif text-sm font-bold text-[#1A2520] mb-3 flex items-center gap-2">
              <TrendingUp className="w-4 h-4 text-[#D48C57]" /> Most Viewed Plots
            </h3>
            <div className="flex flex-wrap gap-2">
              {stats.top_plots.map((p) => (
                <div key={p.plot_id} className="bg-[#F4F1EA] rounded-lg px-3 py-2 text-xs border border-[#E5DFD3]">
                  <span className="font-bold text-[#2C4C3B]">#{p.plot_id}</span>
                  <span className="text-[#5C6A61] ml-1.5">{p.interactions} views</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Pricing Elasticity Alerts */}
        {alerts.length > 0 && (
          <div className="bg-amber-50 rounded-xl border border-amber-200 p-5">
            <h3 className="font-serif text-sm font-bold text-amber-800 mb-3 flex items-center gap-2">
              <TrendingUp className="w-4 h-4 text-amber-600" /> Pricing Elasticity Alerts
            </h3>
            <div className="space-y-2">
              {alerts.map((a) => (
                <div key={a.id} className="flex items-center justify-between bg-white rounded-lg p-3 border border-amber-100">
                  <div>
                    <p className="text-xs font-medium text-[#1A2520]">{a.message}</p>
                    <p className="text-[10px] text-[#5C6A61] mt-0.5">Plot #{a.plot_id} | {a.interactions} interactions | {a.leads} leads | Ratio: {a.ratio}</p>
                  </div>
                  <button onClick={() => dismissAlert(a.id)} className="text-xs text-[#5C6A61] hover:text-[#1A2520] px-2 py-1">Dismiss</button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Plot Status Management */}
        <div className="bg-white rounded-xl border border-[#E5DFD3] overflow-hidden">
          <div className="px-5 py-3 border-b border-[#E5DFD3] bg-[#F4F1EA]">
            <h3 className="font-serif text-sm font-bold text-[#1A2520]">Plot Status Management</h3>
          </div>
          <div className="p-4 grid grid-cols-4 sm:grid-cols-6 md:grid-cols-8 lg:grid-cols-10 gap-2">
            {[1,2,3,4,5,6,7,8,9,10,11,12,13,14,15,16,17,18,19,20,21,24,25,26,27,28,29,30,31,32,33,34,35,36,37,38,39,40,41,42,43,44,45,46,47,48,49,50,51,52,53,54,55,56,57,58,59,60,61].map(id => {
              const currentStatus = plotOverrides[id] || 'default';
              const colors = {
                default: 'bg-gray-50 border-gray-200',
                available: 'bg-emerald-50 border-emerald-300',
                launch: 'bg-amber-50 border-amber-300',
                sold: 'bg-red-50 border-red-300',
              };
              return (
                <div key={id} className={`relative rounded-lg border p-2 text-center ${colors[currentStatus]}`}>
                  <p className="text-xs font-bold text-[#1A2520]">#{id}</p>
                  <select
                    value={currentStatus}
                    onChange={(e) => updatePlotStatus(id, e.target.value)}
                    disabled={statusSaving === id}
                    className="w-full mt-1 text-[9px] bg-transparent border-0 p-0 focus:ring-0 text-[#5C6A61] cursor-pointer"
                  >
                    <option value="default">Default</option>
                    <option value="available">Available</option>
                    <option value="launch">Launch</option>
                    <option value="sold">Sold</option>
                  </select>
                </div>
              );
            })}
          </div>
        </div>

        {/* Vaastu Claims (Priority) */}
        <LeadTable title="Vaastu Priority Claims (Urgent)" data={stats?.recent_vaastu} columns={vaastuCols} />

        {/* Site Visits */}
        <LeadTable title="Recent Site Visit Bookings" data={stats?.recent_visits} columns={visitCols} />

        {/* Leads */}
        <LeadTable title="Recent Leads" data={stats?.recent_leads} columns={leadCols} />
      </div>
    </div>
  );
}

export default function AdminPage() {
  const [admin, setAdmin] = useState(null);

  const handleLogout = async () => {
    try { await axios.post(`${API}/admin/logout`, {}, { withCredentials: true }); } catch {}
    setAdmin(null);
  };

  if (!admin) return <LoginForm onLogin={setAdmin} />;
  return <Dashboard admin={admin} onLogout={handleLogout} />;
}
